import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  Show,
  Create,
  Edit,
  SimpleForm,
  NumberInput,
  BooleanInput,
  required,
  regex,
  useRecordContext,
  useRefresh,
  useNotify,
  Button,
  Toolbar,
  SaveButton,
  type RaRecord,
} from 'react-admin';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { Info, RequestQuote, Storage, Storefront } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { fetchApi } from '@/api/fetchApi';
import { ShowHero, StatCard, SectionPaper, FieldPair, formatTimestamp } from './show-layout';
import { useBreadcrumbSafe } from '@/ui/Breadcrumb';

/**
 * Service Catalogue admin resource — React Admin management surface for the
 * platform pricing source of truth (Milestone 08, sprint 01).
 *
 * Lifecycle: create is an upsert per tenant+serviceKey; deactivation is the
 * only removal path (no DELETE endpoint, `active: false` fails closed in
 * price resolution). Key-shaped params resolve through the active-only path;
 * grid rows pass the document `_id` (server-side fallback, M08 T1.1).
 *
 * @see Documentation/Specification/020_Definitions/application-agnostic/ledger/ServiceCatalogue.md
 * @see Documentation/Specification/04_Processes/application-agnostic/ledger/ServiceCataloguePricing.md
 */

const KEBAB_PATTERN = /^[a-z0-9]+(?:-[a-z0-9-]*[a-z0-9])?$/;

const ZAR_CHOICES = [{ id: 'ZAR', name: 'ZAR' }];

function ActiveChip({ value }: { value: boolean }) {
  return (
    <Chip
      label={value ? 'Active' : 'Inactive'}
      color={value ? 'success' : 'default'}
      size="small"
    />
  );
}

function ActiveChipField({ label }: { label?: string }) {
  const record = useRecordContext<RaRecord>();
  void label;
  if (!record) return null;
  return <ActiveChip value={Boolean(record.active)} />;
}

function PriceField({ record }: { record?: RaRecord }) {
  if (!record?.price) return null;
  return <span>R {Number(record.price.amount).toFixed(2)}</span>;
}

/**
 * Activate/deactivate shortcut (soft lifecycle — the only removal path).
 * Mirrors the identity-verification review-actions fetch style.
 */
function ActiveToggleAction() {
  const record = useRecordContext<RaRecord>();
  const refresh = useRefresh();
  const notify = useNotify();

  if (!record) return null;

  const isActive = Boolean(record.active);
  const verb = isActive ? 'deactivated' : 'reactivated';

  const handleToggle = async () => {
    try {
      const token = localStorage.getItem('rosta_token');
      const base =
        window.__ROSTA_API_URL__ || 'http://localhost:3200/api/v1';
      const res = await fetch(`${base}/service-catalogue/${record.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ active: !isActive }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      notify(`Catalogue entry ${verb}`, { type: 'success' });
      refresh();
    } catch (err) {
      notify(`Failed to update entry: ${(err as Error).message}`, {
        type: 'error',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        label={isActive ? 'Deactivate' : 'Reactivate'}
        variant="outlined"
        color="inherit"
        onClick={handleToggle}
      />
    </Box>
  );
}

/**
 * Live count of service orders for this service key (hero action area +
 * stat tile). Fails soft to an em dash when the admin list endpoint is
 * unreachable or the entitlement is missing.
 */
function useOrdersCount(serviceKey?: string): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!serviceKey) {
      setCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchApi(
          `/service-orders?serviceKey=${encodeURIComponent(serviceKey)}`,
        );
        if (!cancelled) setCount(Array.isArray(data) ? data.length : 0);
      } catch {
        if (!cancelled) setCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceKey]);

  return count;
}

/** Breadcrumb title: entry name (falls back to the stable service key). */
function CatalogueTitle() {
  const record = useRecordContext<RaRecord>();
  if (!record) return 'Service Catalogue';
  return (
    (String(record.name) || String(record.serviceKey) || 'Service Catalogue')
  );
}

function CatalogueFilter(props: object) {
  return (
    <Filter {...props}>
      <TextInput source="serviceKey" label="Service Key" alwaysOn />
      {/* defaultValue is forbidden on filter inputs in react-admin — the
          Active default lives on <List filterDefaultValues> instead. */}
      <SelectInput
        source="active"
        label="State"
        choices={[
          { id: 'true', name: 'Active' },
          { id: 'false', name: 'Inactive' },
        ]}
        alwaysOn
      />
    </Filter>
  );
}

export function ServiceCatalogueList() {
  return (
    <List
      filters={<CatalogueFilter />}
      filterDefaultValues={{ active: 'true' }}
      sort={{ field: 'serviceKey', order: 'ASC' }}
      title="Service Catalogue"
    >
      {/* RA 5.15 reads bulkActionButtons from Datagrid, not List. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="serviceKey" label="Service Key" />
        <TextField source="name" label="Name" />
        <FunctionField label="Price" render={(record: RaRecord) => <PriceField record={record} />} />
        <ActiveChipField label="State" />
        <TextField source="tenant.id" label="Tenant" />
        <TextField source="metadata.updatedAt" label="Updated" />
      </Datagrid>
    </List>
  );
}

function CatalogueContent() {
  const record = useRecordContext<RaRecord>();
  const navigate = useNavigate();
  const ordersCount = useOrdersCount(record ? String(record.serviceKey) : undefined);
  const breadcrumb = useBreadcrumbSafe();
  const setSegments = breadcrumb?.setSegments;
  const clearSegments = breadcrumb?.clearSegments;

  // Replace the auto path crumb (raw URI-encoded document id) with a named
  // trail while this show page is mounted; falls back to path crumbs after
  // navigation (no provider / unmounted).
  useEffect(() => {
    if (!setSegments || !clearSegments) return;
    setSegments([
      { label: 'Home', path: '/' },
      { label: 'Service Catalogues', path: '/service-catalogues' },
      {
        label: String(record?.name || record?.serviceKey || 'Details'),
        path: null,
      },
    ]);
    return () => clearSegments();
  }, [record?.id, record?.name, record?.serviceKey, setSegments, clearSegments]);

  if (!record) return null;

  const openOrders = () => {
    const filter = JSON.stringify({ serviceKey: String(record.serviceKey) });
    navigate(`/service-orders?filter=${encodeURIComponent(filter)}`);
  };

  return (
    <Box sx={{ p: 3, pb: 5 }}>
      <PageDescription
        entity="Service Catalogue"
        description="Pricing source of truth for sponsored services. Each entry sets the chargeable price for one service key; deactivation fails price resolution closed."
        icon={<Storefront />}
      />

      <ShowHero
        icon={<RequestQuote />}
        title={String(record.name) || String(record.serviceKey)}
        subtitle={
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {String(record.serviceKey)}
            <Box component="span" sx={{ fontFamily: 'body1.fontFamily', opacity: 0.75 }}>
              {'  ·  '}
            </Box>
            {String((record.tenant as { id?: string } | undefined)?.id ?? '')}
          </Box>
        }
        chips={
          <>
            <ActiveChip value={Boolean(record.active)} />
            <Chip
              label={String(record.price?.currency || 'ZAR')}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                color: 'inherit',
                fontWeight: 600,
              }}
            />
          </>
        }
        actions={
          <>
            <Button
              label="View orders"
              variant="outlined"
              color="inherit"
              onClick={openOrders}
            />
            <ActiveToggleAction />
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Price">
            <Typography variant="h5" fontWeight={700}>
              R {Number(record.price?.amount ?? 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {String(record.price?.currency || 'ZAR')}
            </Typography>
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="State">
            <ActiveChip value={Boolean(record.active)} />
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Service Orders">
            <Typography
              variant="h5"
              fontWeight={700}
              data-testid="orders-count"
            >
              {ordersCount ?? '—'}
            </Typography>
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Last Updated">
            <Typography variant="body1" fontWeight={600}>
              {formatTimestamp(record.metadata?.updatedAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              by {String(record.metadata?.updatedBy ?? 'system')}
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <SectionPaper icon={<Info />} title="Service">
            <FieldPair label="Name">
              <TextField source="name" label="Name" />
            </FieldPair>
            <FieldPair label="Service Key">
              <TextField source="serviceKey" label="Service Key" />
            </FieldPair>
            <FieldPair label="Description">
              <TextField source="description" label="Description" />
            </FieldPair>
          </SectionPaper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <SectionPaper icon={<Storage />} title="Record & audit">
            <FieldPair label="Tenant">
              <TextField source="tenant.id" label="Tenant" />
            </FieldPair>
            <FieldPair label="URN">
              <TextField source="urn" label="URN" />
            </FieldPair>
            <FieldPair label="Document ID">
              <TextField source="_id" label="Document ID" />
            </FieldPair>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FieldPair label="Created">
                  <Typography variant="body1">
                    {formatTimestamp(
                      String(record.metadata?.createdAt ?? ''),
                    )}
                  </Typography>
                </FieldPair>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FieldPair label="Created By">
                  <TextField source="metadata.createdBy" label="Created By" />
                </FieldPair>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FieldPair label="Updated By">
                  <TextField source="metadata.updatedBy" label="Updated By" />
                </FieldPair>
              </Grid>
            </Grid>
          </SectionPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

export function ServiceCatalogueShow() {
  return (
    <Show title={<CatalogueTitle />}>
      <CatalogueContent />
    </Show>
  );
}

export function ServiceCatalogueCreate() {
  return (
    <Create title="Add Catalogue Entry">
      <SimpleForm defaultValues={{ active: true, price: { currency: 'ZAR' } }}>
        <TextInput
          source="serviceKey"
          label="Service Key"
          validate={[
            required(),
            regex(
              KEBAB_PATTERN,
              'Must be kebab-case: lowercase letters, digits, hyphens (e.g. identity-verification)',
            ),
          ]}
          helperText="Stable key used by order creation (immutable after create)"
          fullWidth
        />
        <TextInput source="name" label="Name" validate={required()} fullWidth />
        <TextInput source="description" label="Description" fullWidth />
        <NumberInput
          source="price.amount"
          label="Price (ZAR)"
          validate={required()}
          min={0}
          fullWidth
        />
        <SelectInput
          source="price.currency"
          label="Currency"
          choices={ZAR_CHOICES}
          validate={required()}
          fullWidth
        />
        <BooleanInput source="active" label="Active" defaultValue={true} />
      </SimpleForm>
    </Create>
  );
}

export function ServiceCatalogueEdit() {
  return (
    <Edit title="Edit Catalogue Entry">
      {/* No delete affordance (D2): soft deactivate is the only removal path.
          RA 5.15's default form toolbar always renders a save and a delete
          button, so the toolbar is overridden to save only. */}
      <SimpleForm toolbar={<Toolbar><SaveButton /></Toolbar>}>
        <TextInput source="name" label="Name" validate={required()} fullWidth />
        <TextInput source="description" label="Description" fullWidth />
        <NumberInput
          source="price.amount"
          label="Price (ZAR)"
          validate={required()}
          min={0}
          fullWidth
        />
        <SelectInput
          source="price.currency"
          label="Currency"
          choices={ZAR_CHOICES}
          validate={required()}
          fullWidth
        />
        <BooleanInput source="active" label="Active" />
        <ActiveToggleAction />
      </SimpleForm>
    </Edit>
  );
}
